/// <reference types="vite/client" />

const DEFAULT_API_BASE_URL = "https://localhost:3002";

/**
 * Browser runtime env values consumed by shared client packages.
 *
 * This is intentionally narrower than an app's full `ImportMetaEnv`: shared
 * packages should depend only on the runtime flags they own.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const env: BrowserRuntimeEnv = { VITE_DENTPLUS_READ_SOURCE: "rpc-live" };
 * ```
 */
export interface BrowserRuntimeEnv {
  readonly [key: string]: string | boolean | undefined;
  readonly DEV?: boolean;
  readonly VITE_API_URL?: string;
  readonly VITE_API_AUTH_PRACTITIONER_URL?: string;
  readonly VITE_COLLABORATION_WS_URL?: string;
  readonly VITE_E2E?: string;
  readonly VITE_ASCLEO_CLIENT_DIAGNOSTICS?: string;
  readonly VITE_DENTPLUS_READ_SOURCE?: "rpc-live";
}

/**
 * Vite-like env input accepted by runtime URL helpers and tests.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const env: ViteLikeEnv = { VITE_API_URL: "https://localhost:3002" };
 * ```
 */
export type ViteLikeEnv = Partial<Record<string, string | boolean | undefined>>;

/**
 * Browser runtime URL diagnostics exposed for local debugging and E2E proof.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const diagnostics = createBrowserRuntimeUrlDiagnostics({
 *   VITE_API_URL: "http://localhost:3000",
 * });
 * ```
 */
export interface BrowserRuntimeUrlDiagnostics {
  readonly apiBaseUrl: string;
  readonly authBaseUrl: string;
  readonly collaborationWsUrl: string;
  readonly rpcUrl: string;
  readonly liveQueryEventsUrl: string;
  readonly readSource: BrowserRuntimeEnv["VITE_DENTPLUS_READ_SOURCE"];
  readonly isE2E: boolean;
  readonly warnings: readonly string[];
}

declare global {
  interface Window {
    __ASCLEO_BROWSER_RUNTIME_URL_DIAGNOSTICS__?: BrowserRuntimeUrlDiagnostics;
  }
}

function isViteLikeEnv(value: unknown): value is ViteLikeEnv {
  return typeof value === "object" && value !== null;
}

/**
 * Read browser runtime env through direct `import.meta.env` access.
 *
 * Vite only replaces direct `import.meta.env` reads in production builds, so
 * all shared client code should use this helper instead of reflection.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const env = getBrowserRuntimeEnv();
 * ```
 */
export function getBrowserRuntimeEnv(): BrowserRuntimeEnv | undefined {
  const rawEnv: unknown = import.meta.env;
  const env = isViteLikeEnv(rawEnv) ? rawEnv : undefined;

  return {
    DEV: readBooleanEnv(env, "DEV"),
    VITE_API_URL: readStringEnv(env, "VITE_API_URL"),
    VITE_API_AUTH_PRACTITIONER_URL: readStringEnv(
      env,
      "VITE_API_AUTH_PRACTITIONER_URL",
    ),
    VITE_COLLABORATION_WS_URL: readStringEnv(
      env,
      "VITE_COLLABORATION_WS_URL",
    ),
    VITE_E2E: readStringEnv(env, "VITE_E2E"),
    VITE_ASCLEO_CLIENT_DIAGNOSTICS: readStringEnv(
      env,
      "VITE_ASCLEO_CLIENT_DIAGNOSTICS",
    ),
    VITE_DENTPLUS_READ_SOURCE: readDentplusReadSourceEnv(env),
  };
}

/**
 * Resolve browser runtime env with same-origin URLs applied for E2E.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const env = createResolvedBrowserRuntimeEnv(import.meta.env);
 * ```
 */
export function createResolvedBrowserRuntimeEnv(
  env: ViteLikeEnv | undefined = getBrowserRuntimeEnv(),
): BrowserRuntimeEnv {
  return {
    DEV: readBooleanEnv(env, "DEV"),
    VITE_API_URL: resolveBrowserApiBaseUrl(env),
    VITE_API_AUTH_PRACTITIONER_URL: resolveBrowserAuthBaseUrl(env),
    VITE_COLLABORATION_WS_URL: resolveBrowserCollaborationWebsocketUrl(env),
    VITE_E2E: readStringEnv(env, "VITE_E2E"),
    VITE_ASCLEO_CLIENT_DIAGNOSTICS: readStringEnv(
      env,
      "VITE_ASCLEO_CLIENT_DIAGNOSTICS",
    ),
    VITE_DENTPLUS_READ_SOURCE: readDentplusReadSourceEnv(env),
  };
}

/**
 * Return whether the current browser env is in Vite development mode.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const dev = isBrowserDevMode();
 * ```
 */
export function isBrowserDevMode(env = getBrowserRuntimeEnv()) {
  return env?.DEV === true;
}

/**
 * Return whether the current browser runtime env selects E2E mode.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * if (isBrowserE2ERuntimeEnabled({ VITE_E2E: "true" })) {
 *   // Expose E2E-only diagnostics.
 * }
 * ```
 */
export function isBrowserE2ERuntimeEnabled(
  env: ViteLikeEnv | undefined = getBrowserRuntimeEnv(),
): boolean {
  return readStringEnv(env, "VITE_E2E") === "true";
}

function canUseWindowOrigin() {
  return (
    typeof window !== "undefined" &&
    typeof window.location?.origin === "string" &&
    /^https?:$/.test(window.location.protocol)
  );
}

function isBrowserE2EMode(env?: ViteLikeEnv) {
  return env?.VITE_E2E === "true" && canUseWindowOrigin();
}

/**
 * Global the E2E harness sets to place the IAM on its own origin.
 *
 * The bundle stamps *sentinel* URLs under `VITE_E2E` (see
 * `e2e/runtime-env.ts`) so that randomised per-run ports cannot poison the
 * content-addressed build cache; everything then resolves from
 * `window.location.origin` at runtime. That is why a delegated topology cannot
 * be expressed through the build: an IAM on its own origin would need a second
 * cached bundle per run.
 *
 * So the harness supplies it at runtime instead, before the app boots. Read
 * only inside the E2E branch — `VITE_E2E` is build-time and false in every
 * shipped bundle, so this cannot be reached in production.
 */
declare global {
  // eslint-disable-next-line no-var
  var __ASCLEO_E2E_IAM_ORIGIN__: string | undefined;
}

function readE2EIamOrigin(): string | undefined {
  const origin = globalThis.__ASCLEO_E2E_IAM_ORIGIN__;
  if (typeof origin !== "string") {
    return undefined;
  }
  const trimmed = origin.trim().replace(/\/$/u, "");
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Resolve the browser-visible API base URL.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const apiUrl = resolveBrowserApiBaseUrl({ VITE_API_URL: "https://localhost:3002" });
 * ```
 */
export function resolveBrowserApiBaseUrl(env?: ViteLikeEnv) {
  if (isBrowserE2EMode(env)) {
    return window.location.origin;
  }

  return readStringEnv(env, "VITE_API_URL") || DEFAULT_API_BASE_URL;
}

/**
 * Resolve the browser-visible practitioner auth base URL.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const authUrl = resolveBrowserAuthBaseUrl();
 * ```
 */
export function resolveBrowserAuthBaseUrl(env?: ViteLikeEnv) {
  if (isBrowserE2EMode(env)) {
    /*
     * A delegated run puts the IAM on its own origin, which is what makes the
     * client present a signed JWT rather than a session handle. Absent the
     * override, auth stays same-origin — every existing lane is unchanged.
     */
    const iamOrigin = readE2EIamOrigin();
    if (iamOrigin !== undefined) {
      return `${iamOrigin}/api/auth/practitioner`;
    }
    return `${window.location.origin}/api/auth/practitioner`;
  }

  return (
    readStringEnv(env, "VITE_API_AUTH_PRACTITIONER_URL") ||
    `${resolveBrowserApiBaseUrl(env)}/api/auth/practitioner`
  );
}

/**
 * Resolve the browser-visible collaboration sidecar WebSocket URL.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const wsUrl = resolveBrowserCollaborationWebsocketUrl();
 * ```
 */
export function resolveBrowserCollaborationWebsocketUrl(env?: ViteLikeEnv) {
  if (isBrowserE2EMode(env)) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/api/collaboration/ws`;
  }

  const websocketUrl = env?.VITE_COLLABORATION_WS_URL;
  if (typeof websocketUrl === "string" && websocketUrl.trim()) {
    return websocketUrl;
  }

  return "ws://localhost:3011";
}

/**
 * Resolve a browser RPC route URL.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const rpcUrl = resolveBrowserRpcUrl("/rpc/fhir/Patient");
 * ```
 */
export function resolveBrowserRpcUrl(routePath = "/rpc", env?: ViteLikeEnv) {
  const normalizedRoutePath = routePath.startsWith("/")
    ? routePath
    : `/${routePath}`;

  return `${resolveBrowserApiBaseUrl(env).replace(/\/+$/, "")}${normalizedRoutePath}`;
}

/**
 * Resolve the browser-visible live-query SSE endpoint.
 *
 * @since 2026-06-16
 * @example
 * ```ts
 * const eventsUrl = resolveBrowserLiveQueryEventsUrl({ VITE_API_URL: "http://localhost:3000" });
 * ```
 */
export function resolveBrowserLiveQueryEventsUrl(env?: ViteLikeEnv) {
  return `${resolveBrowserApiBaseUrl(env).replace(/\/+$/, "")}/api/live-query/events`;
}

/**
 * Build browser runtime URL diagnostics for RPC-live browser troubleshooting.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const diagnostics = createBrowserRuntimeUrlDiagnostics(import.meta.env);
 * console.log(diagnostics.warnings);
 * ```
 */
export function createBrowserRuntimeUrlDiagnostics(
  env: ViteLikeEnv | undefined = getBrowserRuntimeEnv(),
): BrowserRuntimeUrlDiagnostics {
  const apiBaseUrl = resolveBrowserApiBaseUrl(env);
  const authBaseUrl = resolveBrowserAuthBaseUrl(env);
  const collaborationWsUrl = resolveBrowserCollaborationWebsocketUrl(env);
  const rpcUrl = resolveBrowserRpcUrl("/rpc", env);
  const liveQueryEventsUrl = resolveBrowserLiveQueryEventsUrl(env);
  const readSource = readDentplusReadSourceEnv(env);
  const isE2E = isBrowserE2EMode(env);
  const warnings = collectBrowserRuntimeUrlWarnings({
    env,
    apiBaseUrl,
    authBaseUrl,
    collaborationWsUrl,
    rpcUrl,
    liveQueryEventsUrl,
    isE2E,
  });

  return {
    apiBaseUrl,
    authBaseUrl,
    collaborationWsUrl,
    rpcUrl,
    liveQueryEventsUrl,
    readSource,
    isE2E,
    warnings,
  };
}

/**
 * Expose browser runtime URL diagnostics on `window` when diagnostics are enabled.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * installBrowserRuntimeUrlDiagnostics({ VITE_ASCLEO_CLIENT_DIAGNOSTICS: "true" });
 * ```
 */
export function installBrowserRuntimeUrlDiagnostics(
  env: ViteLikeEnv | undefined = getBrowserRuntimeEnv(),
): BrowserRuntimeUrlDiagnostics | null {
  if (!isBrowserRuntimeDiagnosticsEnabled(env) || typeof window === "undefined") {
    return null;
  }

  const diagnostics = createBrowserRuntimeUrlDiagnostics(env);
  Object.assign(window, {
    __ASCLEO_BROWSER_RUNTIME_URL_DIAGNOSTICS__: diagnostics,
  });
  return diagnostics;
}

/**
 * Reset browser runtime URL diagnostics state.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * resetBrowserRuntimeUrlDiagnostics();
 * ```
 */
export function resetBrowserRuntimeUrlDiagnostics(): void {
  if (typeof window !== "undefined") {
    Reflect.deleteProperty(
      window,
      "__ASCLEO_BROWSER_RUNTIME_URL_DIAGNOSTICS__",
    );
  }
}

/**
 * Return whether browser runtime diagnostics should be exposed.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const exposeDiagnostics = isBrowserRuntimeDiagnosticsEnabled({
 *   VITE_ASCLEO_CLIENT_DIAGNOSTICS: "true",
 * });
 * ```
 */
export function isBrowserRuntimeDiagnosticsEnabled(
  env: ViteLikeEnv | undefined = getBrowserRuntimeEnv(),
): boolean {
  return (
    readBooleanEnv(env, "DEV") === true ||
    isBrowserE2ERuntimeEnabled(env) ||
    readStringEnv(env, "VITE_ASCLEO_CLIENT_DIAGNOSTICS") === "true"
  );
}

function collectBrowserRuntimeUrlWarnings(input: {
  readonly env: ViteLikeEnv | undefined;
  readonly apiBaseUrl: string;
  readonly authBaseUrl: string;
  readonly collaborationWsUrl: string;
  readonly rpcUrl: string;
  readonly liveQueryEventsUrl: string;
  readonly isE2E: boolean;
}): readonly string[] {
  const warnings: string[] = [];
  const windowOrigin = canUseWindowOrigin() ? window.location.origin : null;

  if (!readStringEnv(input.env, "VITE_API_URL")) {
    warnings.push("VITE_API_URL is not set; browser runtime is using the default API origin.");
  }

  if (
    windowOrigin &&
    readBooleanEnv(input.env, "DEV") === true &&
    !input.isE2E &&
    readUrlOrigin(input.rpcUrl) === windowOrigin
  ) {
    warnings.push(
      "RPC resolved to the browser dev-server origin outside E2E; configure VITE_API_URL instead of relying on /rpc proxying.",
    );
  }

  addInvalidUrlWarning(warnings, "VITE_API_URL", input.apiBaseUrl);
  addInvalidUrlWarning(
    warnings,
    "VITE_API_AUTH_PRACTITIONER_URL",
    input.authBaseUrl,
  );
  addInvalidUrlWarning(warnings, "VITE_COLLABORATION_WS_URL", input.collaborationWsUrl);
  addInvalidUrlWarning(warnings, "RPC URL", input.rpcUrl);
  addInvalidUrlWarning(
    warnings,
    "Live-query SSE URL",
    input.liveQueryEventsUrl,
  );

  const configuredReadSource = readStringEnv(input.env, "VITE_DENTPLUS_READ_SOURCE");
  if (configuredReadSource && configuredReadSource !== "rpc-live") {
    warnings.push("VITE_DENTPLUS_READ_SOURCE must be rpc-live for DentPlus browser runtime.");
  }

  return warnings;
}

function addInvalidUrlWarning(
  warnings: string[],
  label: string,
  value: string,
): void {
  if (!readUrlOrigin(value)) {
    warnings.push(`${label} is not a valid absolute URL: ${value}`);
  }
}

function readUrlOrigin(value: string): string | null {
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

function readStringEnv(
  env: ViteLikeEnv | undefined,
  key: string,
): string | undefined {
  const value = env?.[key];
  return typeof value === "string" && value.trim() ? value : undefined;
}

function readBooleanEnv(
  env: ViteLikeEnv | undefined,
  key: string,
): boolean | undefined {
  const value = env?.[key];
  return typeof value === "boolean" ? value : undefined;
}

function readDentplusReadSourceEnv(
  _env: ViteLikeEnv | undefined,
): BrowserRuntimeEnv["VITE_DENTPLUS_READ_SOURCE"] {
  return "rpc-live";
}
